import { APIError, BaseAuthService } from "@overmap-ai/core"
import request, { type Response, type SuperAgentRequest } from "superagent"

import { type RootState, setLoggedIn } from "../../store"
import { setSessionExpired } from "../../store"
import type { AppSDK } from "../sdk"

/** The name Django gives the CSRF cookie. Readable from JavaScript: the API sets CSRF_COOKIE_HTTPONLY to False. */
const CSRF_COOKIE_NAME = "csrftoken"

/** The header corsheaders and Django's CsrfViewMiddleware both already expect. */
const CSRF_HEADER_NAME = "X-CSRFToken"

/** Methods Django considers safe, and for which it therefore does not check a CSRF token. */
const CSRF_EXEMPT_METHODS = new Set(["GET", "HEAD", "OPTIONS", "TRACE"])

/**
 * What DRF's SessionAuthentication puts in the `detail` of the 403 it raises for a bad or missing CSRF token.
 *
 * NOTE: This is the only thing separating that 403 from a genuine "you may not do this", which must keep being
 *  discarded rather than retried. `CSRFTokenViewTest` in hemora-api pins it from the other side.
 */
const CSRF_FAILURE_MARKER = "CSRF Failed"

// Signing out waits for the API to end the session. If the API does not answer in this time, the app opens the API's logout
// page instead.
const END_SESSION_TIMEOUT_MS = 5000

function readCookie(name: string): string | undefined {
	let last: string | undefined
	// document.cookie is a single "a=1; b=2" string, and a value may legitimately contain "=", so only the first one
	// separates the name from the value.
	for (const entry of document.cookie.split(";")) {
		const separator = entry.indexOf("=")
		if (separator === -1) continue
		if (entry.slice(0, separator).trim() === name) {
			const value = entry.slice(separator + 1)
			try {
				last = decodeURIComponent(value)
			} catch {
				// Not percent-encoded, or not validly so. Throwing here would escape `applyAuth`, which runs before
				// `performRequest`'s error handling, and fail every request until the cookie changed. Django's token
				// is plain alphanumerics anyway, so the raw value is the right answer, not an error.
				last = value
			}
		}
	}
	return last
}

export class SessionAuthService extends BaseAuthService<RootState, AppSDK> {
	override readonly host: string = import.meta.env.REACT_APP_API_URL

	private lastIssuedCsrfToken: string | undefined

	// De-duplicates the CSRF fetch. `prepareAuth` runs before every request, and a cold start fires many at once.
	private csrfRequest: Promise<void> | undefined

	private csrfTokenIsStale: boolean = false

	public constructor(client: AppSDK) {
		super(client)
	}

	override getAuthHeader(): string {
		// Not used in this scheme; the credential is a cookie, not a header.
		return ""
	}

	override applyAuth(req: SuperAgentRequest): SuperAgentRequest {
		// Without this the browser sends no cookies at all cross-origin, and every request is anonymous. The API allows
		// it: CORS_ALLOW_CREDENTIALS is true and the frontend's origin is in CORS_ORIGIN_WHITELIST.
		let ret = req.withCredentials()

		if (!CSRF_EXEMPT_METHODS.has(req.method.toUpperCase())) {
			// NOTE: The cookie from production is shared with testing.
			//  Therefore, we must prefer the fetched CSRF token over the cookie, or the prod cookie breaks testing.
			const token: string | undefined = this.lastIssuedCsrfToken ?? readCookie(CSRF_COOKIE_NAME)
			if (token) {
				ret = ret.set(CSRF_HEADER_NAME, token)
			}
		}

		return ret
	}

	/**
	 * Makes sure a CSRF token exists before a request needs one.
	 *
	 * Normally there is nothing to do: the API's login page sets the cookie on the way in. The case worth handling is a
	 * browser that kept the session and lost the cookie -- they expire on separate schedules -- which can otherwise
	 * never write again, not even to sign out.
	 */
	override async prepareAuth(): Promise<void> {
		if (!this.csrfTokenIsStale && (readCookie(CSRF_COOKIE_NAME) ?? this.lastIssuedCsrfToken)) return

		// The request goes out through superagent directly rather than through the SDK. Anything the SDK sends calls
		// `prepareAuth` first, and this is `prepareAuth`.
		this.csrfRequest ??= request
			.get(`${this.host}/authentication/csrf/`)
			.withCredentials()
			.then((response: Response) => {
				this.lastIssuedCsrfToken = (response.body as { csrfToken?: string }).csrfToken
				this.csrfTokenIsStale = false
			})
			.catch((error: unknown) => {
				// Offline, or the API is down. Not fatal on its own: the request that follows will fail on its own
				// terms and be retried by the outbox, by which time this may well succeed.
				console.warn("Could not fetch a CSRF token:", error)
			})
			.finally(() => {
				this.csrfRequest = undefined
			})

		return this.csrfRequest
	}

	/**
	 * A response indicated that the user is unauthorized. This service insists that the request is kept regardless.
	 *
	 * There are two ways of "being unauthorized":
	 * 	1) An expired/rejected CSRF token (403 Forbidden).
	 * 	2) An expired session.
	 *
	 * An expired session needs the user's input: Only they can sign in, so `SessionExpiredDialog` asks interactively.
	 *
	 * A rejected CSRF token needs nothing from the user because we can simply fetch a new one. In that case, triggering
	 * a `SessionExpiredDialog` would prompt the user to sign in over something that is about to repair itself,
	 * telling them their session had expired when it had not really.
	 *
	 * We deliberately do not auto-navigate away in either case. Requests are retried in the background, and firing a
	 * redirect such retries would throw the user off whatever page they were on with no warning and nothing saved.
	 */
	override async handleUnauthorized(_req: SuperAgentRequest, response: Response): Promise<void> {
		const sessionIsGone: boolean = response.status === 401

		if (sessionIsGone) {
			this.dispatch(setSessionExpired(true))
		} else {
			this.csrfTokenIsStale = true
			this.lastIssuedCsrfToken = undefined
		}

		throw new APIError({
			message: sessionIsGone
				? "Your session has expired. Please sign in again."
				: "Your security token needed to be refreshed. This will be resent shortly.",
			response,
			// The request is good; only a cookie stale.
			// Discarding here would roll back work the user has already done and drop it from the outbox.
			discard: false,
		})
	}

	override isAuthFailure(response: Response): boolean {
		if (response.status === 401) return true

		// DRF answers a bad CSRF token with 403 PermissionDenied, which is otherwise indistinguishable from a genuine
		// "you may not do this" -- and those must keep being discarded rather than retried forever.
		if (response.status !== 403) return false
		const detail: unknown = (response.body as { detail?: unknown } | undefined)?.detail
		// `includes`, not `startsWith`: the API's JSON renderer hands DRF's `detail` to msgspec, which does not treat
		// a str subclass as a string and falls back to an encoder that JSON-encodes it first -- so the value arrives
		// wrapped in a literal pair of quote characters. Matching on the substring survives that being fixed.
		return typeof detail === "string" && detail.includes(CSRF_FAILURE_MARKER)
	}

	/**
	 * Ends the session by sending a POST request (with the CSRF token) to the API's logout endpoint.
	 * (The form on the API's logout confirmation page sends the same request.)
	 * The API answers with a redirect to the login page.
	 * This method does not follow the redirect because the caller opens the login page next.
	 * @returns Whether the API ended the session. If it did not (for example, when offline, when the token was
	 *  rejected, or when the API did not answer within `END_SESSION_TIMEOUT_MS`), the caller opens the logout
	 *  confirmation page instead.
	 */
	async endSession(): Promise<boolean> {
		const controller = new AbortController()
		let timeoutId: ReturnType<typeof setTimeout> | undefined
		const timeout = new Promise<false>((resolve) => {
			timeoutId = setTimeout(() => {
				controller.abort()
				resolve(false)
			}, END_SESSION_TIMEOUT_MS)
		})
		try {
			return await Promise.race([this.requestLogout(controller.signal), timeout])
		} finally {
			clearTimeout(timeoutId)
		}
	}

	private async requestLogout(signal: AbortSignal): Promise<boolean> {
		await this.prepareAuth()
		const token: string | undefined = this.lastIssuedCsrfToken ?? readCookie(CSRF_COOKIE_NAME)
		if (!token || signal.aborted) return false

		try {
			// Reason for not using `this.performRequest`:
			// `performRequest` goes through superagent, which runs on XHR, and XHR always follows redirects.
			// The API answers a successful logout with a 302 to the login page.
			// A plain `fetch` with `redirect: "manual"` lets us treat that redirect as success (`opaqueredirect`)
			// without loading the login page in the background.
			// The request is also sent while the user is being signed out. Going through `performRequest` would send a
			// rejected token to `handleUnauthorized`, which starts its own redirect to the login page.
			const response = await fetch(`${this.host}/accounts/logout/`, {
				method: "POST",
				credentials: "include",
				headers: { [CSRF_HEADER_NAME]: token },
				redirect: "manual",
				signal,
			})
			return response.type === "opaqueredirect"
		} catch (error: unknown) {
			console.warn("Could not end the session:", error)
			return false
		}
	}

	/**
	 * Signing out, as far as the store is concerned.
	 *
	 * NOTE: Do not dispatch RESET_STATE, which empties the outbox. Session expiry is not sign-out:
	 *  A user whose cookie lapsed while offline still has unsynced work, and it has to survive until they sign back in.
	 *  The session itself is ended by `endSession`.
	 */
	override clearAuth(): void {
		this.dispatch(setLoggedIn(false))
	}

	override initAuth(_payload: object): Promise<void> {
		return Promise.reject(
			new Error(
				"Session authentication has no login of its own. " +
					"Send the browser to buildLoginUrl() and let the API authenticate the user.",
			),
		)
	}
}
